/**
 * Mapbox Custom Layer Service
 * Creates and manages the Three.js custom layer for Mapbox GL JS
 */

import * as THREE from 'three'
import { SceneBuilder, type Building3DConfig } from './SceneBuilder'
import { CoordinateService } from './CoordinateService'
import { getAircraftLoaderInstance, type AircraftLoaderService, type PlacedAircraft3D } from './AircraftLoaderService'
import { ThrottledModelQueue } from './ThrottledModelQueue'
import { renderBuildingFromConfig } from './rendering/renderBuildingFromConfig'
import type { Building3DConfigExtended } from './BuildingLoaderService'
import { ENABLE_COLLISION_DEBUG } from '@/constants/collision'
import { FEET_TO_METERS } from '@/constants/units'
import { ObstacleRenderer, type ObstacleRenderData } from './rendering/ObstacleRenderer'
import { SubunitRenderer, type SubunitRenderData } from './rendering/SubunitRenderer'
import { useRampViewConfigStore } from '@/stores/rampViewConfigStore'

// Debug: Log renderer.info every N ms (draw calls, triangles, GPU memory)
const ENABLE_RENDER_DEBUG = import.meta.env.DEV
const RENDER_DEBUG_INTERVAL = 5000 // 5 seconds between logs
let _lastRenderInfoLog = 0

// Pre-allocated temp matrices to avoid per-frame allocations in render()
const _renderMatrix1 = new THREE.Matrix4()
const _renderMatrix2 = new THREE.Matrix4()
const _renderMatrix3 = new THREE.Matrix4()

// Extended CustomLayerInterface with our custom properties
interface ThreeJsCustomLayer extends mapboxgl.CustomLayerInterface {
  camera: THREE.Camera
  scene: THREE.Scene
  renderer: THREE.WebGLRenderer
  map: mapboxgl.Map
  perspectiveCamera?: THREE.PerspectiveCamera
  handleResize?: () => void
}

export interface SceneObjects {
  buildings: THREE.Group[]
  aircraft: THREE.Object3D[]
  scene: THREE.Scene | null
  camera: THREE.Camera | null
  groundPlane: THREE.Mesh | null
}

export interface CreateLayerParams {
  center: [number, number]  // [lng, lat] - scene origin
  altitude?: number         // meters (default: 0)
  enableShadows?: boolean
  shadowQuality?: 'low' | 'medium' | 'high' | 'ultra'
  buildings?: Building3DConfigExtended[]  // Extended config with template data
  aircraft?: PlacedAircraft3D[]
  onSceneReady?: (sceneObjects: SceneObjects) => void
  onAircraftLoaded?: (aircraft: THREE.Object3D[]) => void
}

export type LODLevel = 'high' | 'low'

export class MapboxCustomLayerService {
  private sceneObjects: SceneObjects = {
    buildings: [],
    aircraft: [],
    scene: null,
    camera: null,
    groundPlane: null
  }
  private aircraftLoader: AircraftLoaderService | null = null
  private map: mapboxgl.Map | null = null

  // Stored rendering objects — survive Mapbox layer removal on style change
  private renderer: THREE.WebGLRenderer | null = null
  private camera: THREE.Camera | null = null
  private perspectiveCamera: THREE.PerspectiveCamera | null = null

  // LOD system — swap between detailed GLTF and simple box by zoom level
  private currentLODLevel: LODLevel = 'high'
  // Cache high-detail models when switched to low, for instant restore
  private highDetailCache = new Map<string, THREE.Object3D>()
  // PlacedAircraft3D data needed to create simplified models. Kept LIVE: the
  // animated pose written via setAircraftTransform() updates these entries so a
  // zoom-triggered LOD rebuild reconstructs each object where it currently is.
  private aircraftDataMap = new Map<string, PlacedAircraft3D>()
  // Ids whose objects are owned imperatively by a caller (not by the allocation
  // data feed). Tracked here as well as on `userData.externalOwner` so an LOD
  // rebuild — which constructs a NEW object — can re-stamp the flag.
  private externalOwnedIds = new Set<string>()
  // Debounce the zoom→LOD swap so a rapid in/out across the threshold doesn't
  // thrash (each swap rebuilds every aircraft object).
  private lodSwapTimer: ReturnType<typeof setTimeout> | null = null
  // When false the zoom→LOD swap is suspended and detail is pinned to 'high'.
  // Cinematic playback engages this so a shot that dips below the zoom threshold
  // doesn't turn every aircraft into a grey box mid-move.
  private lodEnabled = true
  // Throttle Low→High reloads of uncached models so a zoom-in doesn't fetch +
  // instantiate everything in one frame (100ms cached / 500ms network load).
  private lodLoadQueue = new ThrottledModelQueue()

  // Obstacle rendering — per-building obstacle renderers
  private obstacleRenderers = new Map<string, ObstacleRenderer>()

  // Subunit rendering — per-building subunit renderers
  private subunitRenderers = new Map<string, SubunitRenderer>()

  /**
   * Create a Three.js custom layer for Mapbox GL JS
   */
  createThreeJsLayer(params: CreateLayerParams): ThreeJsCustomLayer {
    const {
      center,
      altitude = 0,
      enableShadows = true,
      shadowQuality = 'high',
      buildings = [],
      aircraft = [],
      onSceneReady
    } = params

    const sceneObjects = this.sceneObjects
    // Capture the service instance: the layer's `onAdd`/`render` use `function` syntax because
    // Mapbox invokes them with `this` bound to the layer object (per CustomLayerInterface), so they
    // cannot be arrow functions. This closure lets those callbacks reach the service instance.
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const service = this

    // Use shared singleton aircraft loader (cache is shared with AircraftStep preload + placement mode)
    this.aircraftLoader = getAircraftLoaderInstance()

    const customLayer: ThreeJsCustomLayer = {
      id: 'three-js-layer',
      type: 'custom',
      renderingMode: '3d',
      camera: null as unknown as THREE.Camera,
      scene: null as unknown as THREE.Scene,
      renderer: null as unknown as THREE.WebGLRenderer,
      map: null as unknown as mapboxgl.Map,

      onAdd: function(map, gl) {
        this.camera = new THREE.Camera()
        this.scene = new THREE.Scene()
        this.map = map
        service.map = map

        sceneObjects.scene = this.scene
        sceneObjects.camera = this.camera

        // Create Three.js renderer using Mapbox's WebGL context
        this.renderer = new THREE.WebGLRenderer({
          canvas: map.getCanvas(),
          context: gl,
          antialias: true
        })

        // Configure renderer and lighting
        SceneBuilder.setupRenderer(this.renderer, enableShadows)
        SceneBuilder.setupLighting(this.scene, enableShadows, shadowQuality)

        // Create invisible ground plane for raycasting
        const groundPlane = SceneBuilder.createGroundPlane(5000)
        this.scene.add(groundPlane)
        sceneObjects.groundPlane = groundPlane

        // NOTE: Buildings are loaded incrementally by MapboxScene.tsx via addBuildingFromConfig()
        // NOTE: Aircraft are loaded dynamically by MapboxScene.tsx useEffect
        // Do NOT load buildings or aircraft here to avoid duplication / blocking

        // Create perspective camera for raycasting
        const canvas = map.getCanvas()
        const perspectiveCamera = new THREE.PerspectiveCamera(
          28,
          canvas.width / canvas.height,
          0.1,
          10000
        )
        this.perspectiveCamera = perspectiveCamera
        // CRITICAL: Assign perspectiveCamera to sceneObjects for raycasting
        // The generic THREE.Camera() used for rendering doesn't support raycasting
        sceneObjects.camera = perspectiveCamera

        // Store rendering objects on service instance (survive layer removal on style change)
        service.renderer = this.renderer
        service.camera = this.camera
        service.perspectiveCamera = perspectiveCamera

        // Handle window resize
        const handleResize = () => {
          const newCanvas = map.getCanvas?.()
          if (!newCanvas) return
          perspectiveCamera.aspect = newCanvas.width / newCanvas.height
          perspectiveCamera.updateProjectionMatrix()
          this.renderer?.setSize(newCanvas.width, newCanvas.height, false)
        }
        map.on('resize', handleResize)
        this.handleResize = handleResize

        // Zoom-based shadow quality scaling:
        // At overview zoom (<16), use low-res shadow maps to halve shadow draw cost.
        // At close zoom (≥16), use full resolution for crisp shadows.
        if (enableShadows) {
          let currentShadowTier: 'high' | 'low' = 'high'
          const scene = this.scene
          map.on('zoom', () => {
            const zoom = map.getZoom()
            const newTier = zoom >= 16 ? 'high' : 'low'
            if (newTier === currentShadowTier) return
            currentShadowTier = newTier
            const newSize = newTier === 'high' ? 2048 : 512
            scene.traverse((child) => {
              if (child instanceof THREE.DirectionalLight && child.castShadow) {
                child.shadow.mapSize.set(newSize, newSize)
                // Force shadow map rebuild at new resolution
                if (child.shadow.map) {
                  child.shadow.map.dispose()
                  child.shadow.map = null as unknown as THREE.WebGLRenderTarget
                }
              }
            })
            map.triggerRepaint()
          })
        }

        // LOD zoom listener — swap aircraft detail at zoom threshold.
        // Debounced: wait ~150ms for zoom to settle so a rapid in/out across
        // the threshold doesn't thrash the (object-rebuilding) swap.
        map.on('zoom', () => {
          if (!service.lodEnabled) return
          const zoom = map.getZoom()
          const newLevel: LODLevel = zoom >= 17 ? 'high' : 'low'
          if (newLevel === service.currentLODLevel) return
          if (service.lodSwapTimer != null) clearTimeout(service.lodSwapTimer)
          service.lodSwapTimer = setTimeout(() => {
            service.lodSwapTimer = null
            const settled: LODLevel = map.getZoom() >= 17 ? 'high' : 'low'
            if (settled !== service.currentLODLevel) {
              service.currentLODLevel = settled
              service.swapAircraftLOD(settled)
            }
          }, 150)
        })

        // Calculate scene transformation matrix
        const transformMatrix = CoordinateService.createMapboxTransformMatrix(center, altitude)
        this.scene.matrix = transformMatrix
        this.scene.matrixAutoUpdate = false

        console.log('3D Layer initialized:', {
          center,
          altitude,
          buildingsCount: buildings.length,
          aircraftCount: aircraft.length,
          sceneChildrenCount: this.scene.children.length
        })

        // Notify that scene is ready
        onSceneReady?.(sceneObjects)
      },

      render: function(_gl, matrix) {
        const canvas = this.map.getCanvas?.()
        if (!canvas) return
        const width = canvas.width
        const height = canvas.height

        // Update renderer size if needed
        if (this.renderer.domElement.width !== width || this.renderer.domElement.height !== height) {
          this.renderer.setSize(width, height, false)
        }

        // Use Mapbox's camera projection matrix (reuse pre-allocated matrices)
        _renderMatrix1.fromArray(matrix)
        this.camera.projectionMatrix.copy(_renderMatrix1).multiply(this.scene.matrix)

        // Update perspective camera for raycasting
        if (this.perspectiveCamera) {
          const currentAspect = width / height
          if (Math.abs(this.perspectiveCamera.aspect - currentAspect) > 0.001) {
            this.perspectiveCamera.aspect = currentAspect
            this.perspectiveCamera.updateProjectionMatrix()
          }

          _renderMatrix2.fromArray(matrix).multiply(this.scene.matrix)
          _renderMatrix3.copy(_renderMatrix2).invert()
          this.perspectiveCamera.projectionMatrix.copy(_renderMatrix2)
          this.perspectiveCamera.projectionMatrixInverse.copy(_renderMatrix3)
          this.perspectiveCamera.matrixWorld.copy(_renderMatrix3)
        }

        // Render
        this.renderer.resetState()
        this.renderer.render(this.scene, this.camera)

        // Renderer diagnostics — throttled to avoid console spam
        if (ENABLE_RENDER_DEBUG) {
          const now = Date.now()
          if (now - _lastRenderInfoLog > RENDER_DEBUG_INTERVAL) {
            _lastRenderInfoLog = now
            const info = this.renderer.info
            console.log('🎮 Renderer:', {
              drawCalls: info.render.calls,
              triangles: info.render.triangles,
              geometries: info.memory.geometries,
              textures: info.memory.textures,
            })
          }
        }

        // Only request next frame if scene has pending changes (dirty flag)
        if (this.scene.userData.needsRepaint) {
          this.scene.userData.needsRepaint = false
          this.map.triggerRepaint()
        }
      }
    }

    return customLayer
  }

  /**
   * Whether the service has an active Three.js scene and renderer.
   * Used to decide between reattach (fast) vs full rebuild on style change.
   */
  hasScene(): boolean {
    return this.sceneObjects.scene !== null && this.renderer !== null
  }

  /**
   * Create a lightweight layer that reuses the existing scene/renderer/camera.
   * Used after map.setStyle() to re-register the custom layer without rebuilding
   * the entire Three.js scene graph. Event listeners (zoom, resize) survive
   * style changes because they're registered on the map instance, not the layer.
   */
  createReattachLayer(): ThreeJsCustomLayer {
    // See createThreeJsLayer: the layer's Mapbox callbacks are `function`s bound to the layer
    // object, so we capture the service instance here for them to reference.
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const service = this

    const reattachLayer: ThreeJsCustomLayer = {
      id: 'three-js-layer',
      type: 'custom',
      renderingMode: '3d',
      camera: null as unknown as THREE.Camera,
      scene: null as unknown as THREE.Scene,
      renderer: null as unknown as THREE.WebGLRenderer,
      map: null as unknown as mapboxgl.Map,

      onAdd: function(map, _gl) {
        // Reuse existing objects — no new scene, renderer, buildings, or listeners
        this.camera = service.camera!
        this.scene = service.sceneObjects.scene!
        this.renderer = service.renderer!
        this.perspectiveCamera = service.perspectiveCamera ?? undefined
        this.map = map
        service.map = map

        // Force immediate repaint so the scene renders on the first frame
        // Without this, the scene stays invisible until user interaction if the map is idle
        map.triggerRepaint()

        console.log('3D Layer reattached after style change')
      },

      render: function(_gl, matrix) {
        const canvas = this.map.getCanvas?.()
        if (!canvas) return
        const width = canvas.width
        const height = canvas.height

        // Update renderer size if needed
        if (this.renderer.domElement.width !== width || this.renderer.domElement.height !== height) {
          this.renderer.setSize(width, height, false)
        }

        // Use Mapbox's camera projection matrix (reuse pre-allocated matrices)
        _renderMatrix1.fromArray(matrix)
        this.camera.projectionMatrix.copy(_renderMatrix1).multiply(this.scene.matrix)

        // Update perspective camera for raycasting
        if (this.perspectiveCamera) {
          const currentAspect = width / height
          if (Math.abs(this.perspectiveCamera.aspect - currentAspect) > 0.001) {
            this.perspectiveCamera.aspect = currentAspect
            this.perspectiveCamera.updateProjectionMatrix()
          }

          _renderMatrix2.fromArray(matrix).multiply(this.scene.matrix)
          _renderMatrix3.copy(_renderMatrix2).invert()
          this.perspectiveCamera.projectionMatrix.copy(_renderMatrix2)
          this.perspectiveCamera.projectionMatrixInverse.copy(_renderMatrix3)
          this.perspectiveCamera.matrixWorld.copy(_renderMatrix3)
        }

        // Render
        this.renderer.resetState()
        this.renderer.render(this.scene, this.camera)

        // Renderer diagnostics — throttled to avoid console spam
        if (ENABLE_RENDER_DEBUG) {
          const now = Date.now()
          if (now - _lastRenderInfoLog > RENDER_DEBUG_INTERVAL) {
            _lastRenderInfoLog = now
            const info = this.renderer.info
            console.log('\u{1F3AE} Renderer:', {
              drawCalls: info.render.calls,
              triangles: info.render.triangles,
              geometries: info.memory.geometries,
              textures: info.memory.textures,
            })
          }
        }

        // Only request next frame if scene has pending changes (dirty flag)
        if (this.scene.userData.needsRepaint) {
          this.scene.userData.needsRepaint = false
          this.map.triggerRepaint()
        }
      }
    }

    return reattachLayer
  }

  /**
   * Mark scene as needing a repaint (triggers next render frame)
   * Call this after any scene modification (add/remove objects, material changes, etc.)
   */
  markSceneDirty(): void {
    if (this.sceneObjects.scene) {
      this.sceneObjects.scene.userData.needsRepaint = true
      this.map?.triggerRepaint()
    }
  }

  /**
   * Add a building to the scene
   */
  addBuilding(config: Building3DConfig, enableShadows: boolean = true): THREE.Group | null {
    if (!this.sceneObjects.scene) {
      console.warn('Scene not initialized yet')
      return null
    }

    const buildingGroup = SceneBuilder.createBuilding(config, enableShadows)
    this.sceneObjects.scene.add(buildingGroup)
    this.sceneObjects.buildings.push(buildingGroup)
    this.applyRoofVisibility(buildingGroup)
    this.markSceneDirty()

    return buildingGroup
  }

  /**
   * Add a building from an extended config (template-aware rendering).
   * Used by the progressive building loading pipeline.
   */
  addBuildingFromConfig(
    config: Building3DConfigExtended,
    opts: { enableShadows?: boolean } = {}
  ): THREE.Group | null {
    if (!this.sceneObjects.scene) {
      console.warn('Scene not initialized yet')
      return null
    }

    const buildingGroup = renderBuildingFromConfig(config, {
      enableShadows: opts.enableShadows ?? true,
    })
    this.sceneObjects.scene.add(buildingGroup)
    this.sceneObjects.buildings.push(buildingGroup)
    this.applyRoofVisibility(buildingGroup)
    this.markSceneDirty()

    return buildingGroup
  }

  /**
   * Remove a building from the scene by ID
   */
  removeBuilding(buildingId: string): boolean {
    if (!this.sceneObjects.scene) return false

    const index = this.sceneObjects.buildings.findIndex(
      (b) => b.userData.buildingId === buildingId
    )

    if (index !== -1) {
      const building = this.sceneObjects.buildings[index]
      this.disposeObject(building)
      this.sceneObjects.scene.remove(building)
      this.sceneObjects.buildings.splice(index, 1)
      this.markSceneDirty()
      return true
    }

    return false
  }

  /**
   * Live-update a building group's transform in place — used by the event
   * planner's zone editor to move / rotate / resize an area's rendered footprint
   * without a remove + re-add. Mirrors how the loader applies the config:
   * position in scene FEET (converted to meters here, exactly like the building
   * orchestrators — the group's transform lives in meters; y is preserved),
   * rotation as degToRad(rotationDeg) on Y, and an X/Z group scale (footprint pads
   * are flat slabs centered on the group origin, so scaling resizes them about
   * their center). Only the provided fields change. Returns false when no building
   * with that id is in the scene (e.g. the event planner's synthetic fallback
   * apron, which has no mesh).
   */
  updateBuildingTransform(
    buildingId: string,
    transform: { x?: number; z?: number; rotationDeg?: number; scaleX?: number; scaleZ?: number }
  ): boolean {
    const group = this.sceneObjects.buildings.find(
      (b) => b.userData.buildingId === buildingId
    )
    if (!group) return false
    // x/z arrive in scene feet; the rendered group is positioned in meters.
    if (transform.x !== undefined) group.position.x = transform.x * FEET_TO_METERS
    if (transform.z !== undefined) group.position.z = transform.z * FEET_TO_METERS
    if (transform.rotationDeg !== undefined) {
      group.rotation.y = THREE.MathUtils.degToRad(transform.rotationDeg)
    }
    if (transform.scaleX !== undefined) group.scale.x = transform.scaleX
    if (transform.scaleZ !== undefined) group.scale.z = transform.scaleZ
    this.markSceneDirty()
    return true
  }

  /**
   * Clear all buildings from the scene
   */
  clearBuildings(): void {
    if (!this.sceneObjects.scene) return

    this.sceneObjects.buildings.forEach((building) => {
      // Clean up obstacle and subunit renderers for this building
      const buildingId = building.userData.buildingId as string
      if (buildingId) {
        this.removeBuildingObstacles(buildingId)
        this.removeBuildingSubunits(buildingId)
      }

      this.disposeObject(building)
      this.sceneObjects.scene!.remove(building)
    })
    this.sceneObjects.buildings = []
    this.markSceneDirty()
  }

  /**
   * Set visibility of all building roof groups.
   * Any THREE.Object3D named "roof" in building hierarchies will be toggled.
   */
  setRoofVisibility(visible: boolean): void {
    for (const building of this.sceneObjects.buildings) {
      building.traverse((child) => {
        if (child.name === 'roof') {
          child.visible = visible
        }
      })
    }
    this.markSceneDirty()
  }

  /**
   * Apply current showRoofs state to a single building group.
   * Called when a building is added so it respects the persisted toggle.
   */
  private applyRoofVisibility(buildingGroup: THREE.Group): void {
    const { showRoofs } = useRampViewConfigStore.getState()
    if (!showRoofs) {
      buildingGroup.traverse((child) => {
        if (child.name === 'roof') {
          child.visible = false
        }
      })
    }
  }

  /**
   * Get scene objects for external access
   */
  getSceneObjects(): SceneObjects {
    return this.sceneObjects
  }

  /**
   * Add aircraft to the scene.
   *
   * `opts.externalOwner` marks the object as owned by the caller (an imperative
   * actor, e.g. the event planner's synthetic fleet) so the allocation-diff in
   * `MapboxScene` leaves it alone instead of deleting it as "not in the data".
   */
  async addAircraft(
    aircraftData: PlacedAircraft3D,
    opts?: { externalOwner?: boolean }
  ): Promise<THREE.Object3D | null> {
    if (!this.sceneObjects.scene || !this.aircraftLoader) {
      console.warn('Scene or aircraft loader not initialized yet')
      return null
    }

    // Store aircraft data for LOD rebuilds
    this.aircraftDataMap.set(aircraftData.id, aircraftData)
    if (opts?.externalOwner) this.externalOwnedIds.add(aircraftData.id)

    // If we're at low LOD, add simplified model directly
    if (this.currentLODLevel === 'low') {
      const simplified = this.aircraftLoader.createSimplifiedModel(aircraftData)
      if (opts?.externalOwner) simplified.userData.externalOwner = true
      this.sceneObjects.scene.add(simplified)
      this.sceneObjects.aircraft.push(simplified)
      this.markSceneDirty()
      return simplified
    }

    const model = await this.aircraftLoader.loadAircraft(aircraftData)
    if (opts?.externalOwner) model.userData.externalOwner = true
    this.sceneObjects.scene.add(model)
    this.sceneObjects.aircraft.push(model)
    this.markSceneDirty()

    return model
  }

  /**
   * Remove aircraft from the scene by allocation ID.
   *
   * Removes EVERY object carrying that name, not just the first match: a racing
   * add (the loader is async, so an insert can land after its cancel) can leave
   * a second object with the same name, and a single-match removal would strand
   * it in the scene forever with no way to address it.
   */
  removeAircraft(allocationId: string): boolean {
    if (!this.sceneObjects.scene) return false

    const matches = this.sceneObjects.aircraft.filter((a) => a.name === allocationId)
    if (matches.length === 0) return false

    for (const aircraft of matches) {
      this.disposeObject(aircraft)
      this.sceneObjects.scene.remove(aircraft)
    }
    this.sceneObjects.aircraft = this.sceneObjects.aircraft.filter(
      (a) => a.name !== allocationId
    )
    // Clean up LOD caches
    this.aircraftDataMap.delete(allocationId)
    this.externalOwnedIds.delete(allocationId)
    const cached = this.highDetailCache.get(allocationId)
    if (cached) {
      this.disposeObject(cached)
      this.highDetailCache.delete(allocationId)
    }
    this.markSceneDirty()
    return true
  }

  /**
   * Show/hide a tracked scene object (aircraft or imperatively-added actor).
   *
   * The membership primitive for callers that cycle objects in and out of view
   * many times — toggling `visible` costs nothing, where remove/re-add rebuilds
   * a GLTF clone (with deep-cloned geometry + materials) every time.
   * Returns false when no object with that id is in the scene.
   */
  setSceneObjectVisible(id: string, visible: boolean): boolean {
    let found = false
    for (const obj of this.sceneObjects.aircraft) {
      if (obj.name !== id) continue
      found = true
      if (obj.visible !== visible) {
        obj.visible = visible
        this.markSceneDirty()
      }
    }
    // Keep a cached high-detail copy (parked by the LOD swap) in sync so it
    // restores with the visibility it had when it was cached.
    const cached = this.highDetailCache.get(id)
    if (cached) cached.visible = visible
    return found
  }

  /** Ids of every tracked scene object (aircraft + imperative actors). */
  getSceneObjectIds(): string[] {
    return this.sceneObjects.aircraft.map((a) => a.name)
  }

  /**
   * Remove every tracked scene object whose id matches `predicate`.
   * Used for scoped bulk purges (e.g. "reset the event planner's stage")
   * without touching objects owned by other subsystems. Returns the count.
   */
  removeSceneObjectsWhere(predicate: (id: string) => boolean): number {
    const ids = new Set<string>()
    for (const obj of this.sceneObjects.aircraft) {
      if (predicate(obj.name)) ids.add(obj.name)
    }
    let removed = 0
    for (const id of ids) {
      if (this.removeAircraft(id)) removed++
    }
    return removed
  }

  /**
   * Re-apply a transform to an aircraft already in the scene (animation).
   * Position is in feet [x, y, z] (y carries altitude); rotation in degrees.
   * Reuses the loader's transform-base so taxiing aircraft match parked ones.
   * Does NOT mark the scene dirty — callers animating many objects should call
   * markSceneDirty() once per frame after updating the moving subset.
   */
  setAircraftTransform(
    id: string,
    positionFeet: [number, number, number],
    rotationDeg: number
  ): boolean {
    if (!this.aircraftLoader) return false
    const obj = this.sceneObjects.aircraft.find((a) => a.name === id)
    if (!obj) return false
    this.aircraftLoader.updateModelTransform(obj, positionFeet, rotationDeg)
    // Keep the LOD-rebuild snapshot in sync with the live animated pose so a
    // zoom-triggered swap reconstructs the object where it currently is, not
    // where it was first added.
    const data = this.aircraftDataMap.get(id)
    if (data) {
      data.position = [positionFeet[0], positionFeet[1], positionFeet[2]]
      data.rotation = rotationDeg
    }
    return true
  }

  /**
   * Add a pre-built Object3D (e.g. a procedural tug) to the scene, tracked
   * alongside aircraft so `setAircraftTransform` / `removeAircraft` work on it
   * by `name`. The object must carry `userData.transformBase` if it will be
   * driven by `updateModelTransform`. Returns false if the scene isn't ready.
   */
  addSceneObject(object: THREE.Object3D, id: string): boolean {
    if (!this.sceneObjects.scene) return false
    object.name = id
    // Always caller-owned: nothing in the allocation data feed can produce one,
    // so the diff there must never treat it as a stale row to delete.
    object.userData.externalOwner = true
    this.externalOwnedIds.add(id)
    this.sceneObjects.scene.add(object)
    this.sceneObjects.aircraft.push(object)
    this.markSceneDirty()
    return true
  }

  /**
   * Clear all aircraft from the scene
   */
  clearAircraft(): void {
    if (!this.sceneObjects.scene) return

    // Drop any queued LOD reloads so they don't resurrect cleared aircraft.
    this.lodLoadQueue.clear()
    if (this.lodSwapTimer != null) {
      clearTimeout(this.lodSwapTimer)
      this.lodSwapTimer = null
    }

    this.sceneObjects.aircraft.forEach((aircraft) => {
      this.disposeObject(aircraft)
      this.sceneObjects.scene!.remove(aircraft)
    })
    this.sceneObjects.aircraft = []
    // Clean LOD caches
    for (const [, cached] of this.highDetailCache) {
      this.disposeObject(cached)
    }
    this.highDetailCache.clear()
    this.aircraftDataMap.clear()
    this.externalOwnedIds.clear()
    // Clear diff-tracking sets so stale IDs from old location don't leak into new
    this.previousCollidingIds.clear()
    this.previousHighlightedIds.clear()
    this.originalMaterialCache.clear()
    this.originalOpacityCache.clear()
    // NOTE: Do NOT clear the shared model cache here — it persists for the session
    // so preloaded and previously-loaded models remain available for future use
    this.markSceneDirty()
  }

  /**
   * Get aircraft loader for external use
   */
  getAircraftLoader(): AircraftLoaderService | null {
    return this.aircraftLoader
  }

  /**
   * Get the current LOD level
   */
  getLODLevel(): LODLevel {
    return this.currentLODLevel
  }

  /**
   * Suspend/resume the zoom-driven LOD swap.
   *
   * Disabling pins detail at 'high' (cancelling any pending swap and restoring
   * immediately if we were already low), so a cinematic camera move that crosses
   * the zoom-17 threshold never shows the grey placeholder boxes. Re-enabling
   * does NOT force a re-evaluation — the next zoom event settles it, which is
   * what we want when playback ends on a wide shot.
   */
  setLODEnabled(enabled: boolean): void {
    this.lodEnabled = enabled
    if (enabled) return

    if (this.lodSwapTimer != null) {
      clearTimeout(this.lodSwapTimer)
      this.lodSwapTimer = null
    }
    if (this.currentLODLevel !== 'high') {
      this.currentLODLevel = 'high'
      this.swapAircraftLOD('high')
    }
  }

  /**
   * Swap all aircraft between high-detail (GLTF) and low-detail (box) models.
   * High-detail models are cached (not disposed) for instant restore.
   */
  private swapAircraftLOD(level: LODLevel): void {
    if (!this.sceneObjects.scene || !this.aircraftLoader) return

    const scene = this.sceneObjects.scene

    if (level === 'low') {
      // High → Low: cache high-detail models, replace with boxes
      const newAircraft: THREE.Object3D[] = []
      for (const model of this.sceneObjects.aircraft) {
        const id = model.name
        const data = this.aircraftDataMap.get(id)
        if (!data) {
          // No data to rebuild — keep as-is
          newAircraft.push(model)
          continue
        }
        // Cache the high-detail model (remove from scene, don't dispose)
        scene.remove(model)
        this.highDetailCache.set(id, model)
        // Create simplified replacement, carrying over the live presentation
        // state — a hidden actor must NOT pop back into view on a zoom swap.
        const simplified = this.aircraftLoader!.createSimplifiedModel(data)
        simplified.visible = model.visible
        if (this.externalOwnedIds.has(id)) simplified.userData.externalOwner = true
        scene.add(simplified)
        newAircraft.push(simplified)
      }
      this.sceneObjects.aircraft = newAircraft
    } else {
      // Low → High: restore cached high-detail models
      const newAircraft: THREE.Object3D[] = []
      for (const model of this.sceneObjects.aircraft) {
        const id = model.name
        const cached = this.highDetailCache.get(id)
        if (cached) {
          // Restore high-detail from cache
          scene.remove(model)
          this.disposeObject(model) // dispose the simplified box
          scene.add(cached)
          newAircraft.push(cached)
          this.highDetailCache.delete(id)
        } else {
          // No cached high-detail. Keep the CURRENT box as a live placeholder
          // (it stays correctly posed via the live aircraftDataMap) and enqueue
          // a throttled reload that REPLACES it by id when the model is ready.
          // Do not dispose `model` now — it remains visible until its turn.
          const data = this.aircraftDataMap.get(id)
          if (data) {
            newAircraft.push(model)
            this.enqueueHighDetailReload(id)
          } else {
            newAircraft.push(model)
          }
        }
      }
      this.sceneObjects.aircraft = newAircraft
    }

    this.markSceneDirty()
    console.log(`LOD: Switched to ${level} (${this.sceneObjects.aircraft.length} aircraft)`)
  }

  /**
   * Queue a throttled reload of an aircraft's high-detail model, replacing its
   * placeholder box (found by id) once ready. Cache hits are spaced 100ms
   * apart, network loads 500ms, so a zoom-in across many uncached aircraft
   * doesn't fetch + instantiate everything in one frame. Bails out cleanly if
   * the aircraft is removed or zoom drops back to low LOD during the wait.
   */
  /**
   * Configure the inter-insert spacing of the zoom-driven high-detail reload
   * queue. Both `0` disables throttling (reloads drain as fast as possible).
   * Driven by the event planner's Motion settings.
   */
  setModelInsertThrottle(cachedMs: number, loadMs: number): void {
    this.lodLoadQueue.setDelays(cachedMs, loadMs)
  }

  private enqueueHighDetailReload(id: string): void {
    this.lodLoadQueue.enqueue(id, async () => {
      const scene = this.sceneObjects.scene
      const data = this.aircraftDataMap.get(id)
      // No longer wanted, or we've zoomed back out — abandon.
      if (!scene || !data || !this.aircraftLoader || this.currentLODLevel !== 'high') {
        return { neededLoad: false }
      }
      const neededLoad = this.aircraftLoader.needsNetworkLoad(data)
      const highDetail = await this.aircraftLoader.loadAircraft(data)
      // Re-check after the (possibly async) load.
      if (
        !this.sceneObjects.scene ||
        !this.aircraftDataMap.has(id) ||
        this.currentLODLevel !== 'high'
      ) {
        this.disposeObject(highDetail)
        return { neededLoad }
      }
      // Replace the placeholder box (located by id) with the real model,
      // inheriting its visibility + ownership so the swap is invisible to the
      // caller driving show/hide.
      const cur = this.sceneObjects.aircraft.find((a) => a.name === id)
      if (this.externalOwnedIds.has(id)) highDetail.userData.externalOwner = true
      if (cur) {
        highDetail.visible = cur.visible
        this.sceneObjects.scene.remove(cur)
        this.disposeObject(cur)
        const idx = this.sceneObjects.aircraft.indexOf(cur)
        if (idx !== -1) this.sceneObjects.aircraft[idx] = highDetail
        else this.sceneObjects.aircraft.push(highDetail)
      } else {
        this.sceneObjects.aircraft.push(highDetail)
      }
      this.sceneObjects.scene.add(highDetail)
      this.markSceneDirty()
      return { neededLoad }
    })
  }

  /**
   * Dispose all Three.js GPU resources (geometry, materials, textures) for an object tree.
   * Skips meshes flagged `userData.shared` (e.g. the shared LOD-box geometry/material).
   */
  private disposeObject(obj: THREE.Object3D): void {
    obj.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.userData?.shared) return
        child.geometry?.dispose()
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        mats.forEach((m) => {
          if (m.map) m.map.dispose()
          if (m.normalMap) m.normalMap.dispose()
          if (m.roughnessMap) m.roughnessMap.dispose()
          if (m.metalnessMap) m.metalnessMap.dispose()
          if (m.aoMap) m.aoMap.dispose()
          m.dispose()
        })
      }
    })
  }

  // Store previous collision set for diffing (only update CHANGED aircraft)
  private previousCollidingIds = new Set<string>()
  // Shared collision material (single instance, not cloned per mesh)
  private collisionMaterial = new THREE.MeshStandardMaterial({
    color: 0xff0000,
    transparent: true,
    opacity: 0.7,
  })
  // Cache original materials by mesh UUID for reliable restoration
  private originalMaterialCache = new Map<string, THREE.Material>()

  /**
   * Update aircraft collision visual state
   * Uses diffing to only update aircraft whose state CHANGED (like demo app)
   * Stores original materials in mesh.userData for reliable restoration
   * @param collidingAircraftIds Set of allocation IDs that are currently colliding
   */
  updateAircraftCollisionState(collidingAircraftIds: Set<string>): void {
    if (!this.sceneObjects.scene) return

    // STEP 1: Find ONLY aircraft whose collision state CHANGED
    const changedAircraftIds = new Set<string>()

    // Newly colliding (in current, not in previous)
    for (const id of collidingAircraftIds) {
      if (!this.previousCollidingIds.has(id)) {
        changedAircraftIds.add(id)
      }
    }

    // No longer colliding (in previous, not in current)
    for (const id of this.previousCollidingIds) {
      if (!collidingAircraftIds.has(id)) {
        changedAircraftIds.add(id)
      }
    }

    if (ENABLE_COLLISION_DEBUG) {
      console.log('🎨 updateAircraftCollisionState called:', {
        currentColliding: Array.from(collidingAircraftIds),
        previousColliding: Array.from(this.previousCollidingIds),
        changed: Array.from(changedAircraftIds),
        totalAircraft: this.sceneObjects.aircraft.length,
      })
    }

    // STEP 2: Update ONLY changed aircraft
    let appliedCount = 0
    let restoredCount = 0

    for (const aircraft of this.sceneObjects.aircraft) {
      const allocationId = aircraft.name

      // Skip if state hasn't changed for this aircraft
      if (!changedAircraftIds.has(allocationId)) continue

      const isColliding = collidingAircraftIds.has(allocationId)
      this.updateSingleAircraftMaterial(aircraft, isColliding)

      if (isColliding) {
        appliedCount++
      } else {
        restoredCount++
      }
    }

    if (ENABLE_COLLISION_DEBUG && changedAircraftIds.size > 0) {
      console.log('🎨 Material update summary:', {
        applied: appliedCount,
        restored: restoredCount,
      })
    }

    // STEP 3: Store current state for next diff
    this.previousCollidingIds = new Set(collidingAircraftIds)

    // Mark scene dirty if anything changed
    if (changedAircraftIds.size > 0) {
      this.markSceneDirty()
    }
  }

  /**
   * Update material for a single aircraft based on collision state
   * Uses a shared collision material and caches originals by mesh UUID
   */
  private updateSingleAircraftMaterial(aircraft: THREE.Object3D, isColliding: boolean): void {
    const allocationId = aircraft.name

    aircraft.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return
      if (!child.material) return

      if (isColliding) {
        // Cache original material before replacing (only if not already cached)
        if (!this.originalMaterialCache.has(child.uuid)) {
          this.originalMaterialCache.set(child.uuid, child.material as THREE.Material)
        }
        // Use shared collision material (no cloning)
        child.material = this.collisionMaterial

        if (ENABLE_COLLISION_DEBUG) {
          console.log(`🔴 Applied collision material to: ${allocationId}`)
        }
      } else {
        // Restore original material from cache
        const original = this.originalMaterialCache.get(child.uuid)
        if (original) {
          child.material = original
          this.originalMaterialCache.delete(child.uuid)
        }

        if (ENABLE_COLLISION_DEBUG) {
          console.log(`🔄 Restored original material for: ${allocationId}`)
        }
      }
    })
  }

  /**
   * Clear all collision visual state
   */
  clearCollisionState(): void {
    for (const aircraft of this.sceneObjects.aircraft) {
      this.updateSingleAircraftMaterial(aircraft, false)
    }
    this.previousCollidingIds.clear()
  }

  // Track previous highlight set for diffing
  private previousHighlightedIds = new Set<string>()
  // Cache original opacities by mesh UUID
  private originalOpacityCache = new Map<string, { opacity: number; transparent: boolean }>()

  /**
   * Update aircraft highlight visual state (dim non-highlighted aircraft)
   * When highlightedIds is empty, all aircraft are restored to full opacity.
   * When non-empty, highlighted aircraft get full opacity, others get 20%.
   * @param highlightedIds Set of allocation IDs that should be highlighted
   */
  updateAircraftHighlightState(highlightedIds: Set<string>): void {
    if (!this.sceneObjects.scene) return

    const hasHighlights = highlightedIds.size > 0
    const hadHighlights = this.previousHighlightedIds.size > 0

    // If nothing changed, skip
    if (!hasHighlights && !hadHighlights) return

    for (const aircraft of this.sceneObjects.aircraft) {
      const id = aircraft.name
      const wasHighlighted = !hadHighlights || this.previousHighlightedIds.has(id)
      const isHighlighted = !hasHighlights || highlightedIds.has(id)

      // Skip if state hasn't changed for this aircraft
      if (wasHighlighted === isHighlighted) continue

      aircraft.traverse((child) => {
        if (!(child instanceof THREE.Mesh) || !child.material) return
        const mat = child.material as THREE.MeshStandardMaterial

        if (isHighlighted) {
          // Restore original opacity
          const cached = this.originalOpacityCache.get(child.uuid)
          if (cached) {
            mat.opacity = cached.opacity
            mat.transparent = cached.transparent
            mat.needsUpdate = true
            this.originalOpacityCache.delete(child.uuid)
          }
        } else {
          // Cache original and dim
          if (!this.originalOpacityCache.has(child.uuid)) {
            this.originalOpacityCache.set(child.uuid, {
              opacity: mat.opacity,
              transparent: mat.transparent,
            })
          }
          mat.opacity = 0.2
          mat.transparent = true
          mat.needsUpdate = true
        }
      })
    }

    this.previousHighlightedIds = new Set(highlightedIds)
    this.markSceneDirty()
  }

  /**
   * Clear all highlight visual state (restore all aircraft to full opacity)
   */
  clearHighlightState(): void {
    this.updateAircraftHighlightState(new Set())
  }

  // =========================================================================
  // OBSTACLE RENDERING
  // =========================================================================

  /**
   * Update obstacles for a building.
   * Creates or updates the ObstacleRenderer attached to the building group.
   */
  updateBuildingObstacles(buildingId: string, renderData: ObstacleRenderData[]): void {
    if (!this.sceneObjects.scene) return

    // Find the building group
    const buildingGroup = this.sceneObjects.buildings.find(
      (b) => b.userData.buildingId === buildingId
    )
    if (!buildingGroup) return

    let renderer = this.obstacleRenderers.get(buildingId)

    if (!renderer) {
      renderer = new ObstacleRenderer()
      this.obstacleRenderers.set(buildingId, renderer)
      buildingGroup.add(renderer.getGroup())
    }

    renderer.updateObstacles(renderData)
    this.markSceneDirty()
    // Safety net: ensure repaint even if triggerRepaint was called mid-render
    if (this.map) {
      requestAnimationFrame(() => this.map?.triggerRepaint())
    }
  }

  /**
   * Remove obstacle renderer for a building
   */
  removeBuildingObstacles(buildingId: string): void {
    const renderer = this.obstacleRenderers.get(buildingId)
    if (!renderer) return

    const buildingGroup = this.sceneObjects.buildings.find(
      (b) => b.userData.buildingId === buildingId
    )
    if (buildingGroup) {
      buildingGroup.remove(renderer.getGroup())
    }

    renderer.dispose()
    this.obstacleRenderers.delete(buildingId)
    this.markSceneDirty()
  }

  /**
   * Get obstacle renderer for hit testing
   */
  getObstacleRenderer(buildingId: string): ObstacleRenderer | undefined {
    return this.obstacleRenderers.get(buildingId)
  }

  /**
   * Get all obstacle renderers
   */
  getAllObstacleRenderers(): Map<string, ObstacleRenderer> {
    return this.obstacleRenderers
  }

  /**
   * Show/hide all obstacle renderers (3D mesh visibility toggle)
   */
  setObstaclesVisible(visible: boolean): void {
    for (const [, renderer] of this.obstacleRenderers) {
      renderer.getGroup().visible = visible
    }
    this.markSceneDirty()
  }

  // ========================================================================
  // Subunit rendering
  // ========================================================================

  /**
   * Update subunits for a building.
   * Creates or updates the SubunitRenderer attached to the building group.
   */
  updateBuildingSubunits(buildingId: string, renderData: SubunitRenderData[]): void {
    if (!this.sceneObjects.scene) return

    const buildingGroup = this.sceneObjects.buildings.find(
      (b) => b.userData.buildingId === buildingId
    )
    if (!buildingGroup) return

    let renderer = this.subunitRenderers.get(buildingId)

    if (!renderer) {
      renderer = new SubunitRenderer()
      this.subunitRenderers.set(buildingId, renderer)
      buildingGroup.add(renderer.getGroup())
    }

    renderer.updateSubunits(renderData)
    this.markSceneDirty()
    if (this.map) {
      requestAnimationFrame(() => this.map?.triggerRepaint())
    }
  }

  /**
   * Remove subunit renderer for a building
   */
  removeBuildingSubunits(buildingId: string): void {
    const renderer = this.subunitRenderers.get(buildingId)
    if (!renderer) return

    const buildingGroup = this.sceneObjects.buildings.find(
      (b) => b.userData.buildingId === buildingId
    )
    if (buildingGroup) {
      buildingGroup.remove(renderer.getGroup())
    }

    renderer.dispose()
    this.subunitRenderers.delete(buildingId)
    this.markSceneDirty()
  }

  /**
   * Show/hide all subunit renderers
   */
  setSubunitsVisible(visible: boolean): void {
    for (const [, renderer] of this.subunitRenderers) {
      renderer.getGroup().visible = visible
    }
    this.markSceneDirty()
  }

  /**
   * Fully dispose this service instance: renderer, scene graph, caches.
   * Call before dropping the reference to avoid WebGL context corruption
   * when a new renderer is created on the same Mapbox canvas/context.
   */
  dispose(): void {
    // Dispose all buildings and aircraft in the scene
    this.clearBuildings()
    this.sceneObjects.aircraft.forEach((a) => {
      this.disposeObject(a)
      this.sceneObjects.scene?.remove(a)
    })
    this.sceneObjects.aircraft = []

    // Dispose ground plane
    if (this.sceneObjects.groundPlane) {
      this.disposeObject(this.sceneObjects.groundPlane)
      this.sceneObjects.scene?.remove(this.sceneObjects.groundPlane)
      this.sceneObjects.groundPlane = null
    }

    // Dispose all remaining scene children (lights, helpers, etc.)
    if (this.sceneObjects.scene) {
      while (this.sceneObjects.scene.children.length > 0) {
        const child = this.sceneObjects.scene.children[0]
        this.sceneObjects.scene.remove(child)
        this.disposeObject(child)
      }
      this.sceneObjects.scene = null
    }

    // Dispose the renderer (releases internal WebGL state without destroying the shared context)
    if (this.renderer) {
      this.renderer.dispose()
      this.renderer = null
    }

    // Clear caches
    this.highDetailCache.forEach((model) => this.disposeObject(model))
    this.highDetailCache.clear()
    this.aircraftDataMap.clear()
    this.originalMaterialCache.clear()
    this.originalOpacityCache.clear()
    this.previousCollidingIds.clear()
    this.previousHighlightedIds.clear()
    this.collisionMaterial.dispose()

    // Dispose obstacle renderers
    for (const [, renderer] of this.obstacleRenderers) {
      renderer.dispose()
    }
    this.obstacleRenderers.clear()

    // Dispose subunit renderers
    for (const [, renderer] of this.subunitRenderers) {
      renderer.dispose()
    }
    this.subunitRenderers.clear()

    this.sceneObjects.camera = null
    this.camera = null
    this.perspectiveCamera = null
    this.map = null
    this.aircraftLoader = null
  }
}
